import type { RecurringDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { dayTitle, money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { parseAmount } from "../amount.ts";
import { AmountField } from "./AmountField.tsx";
import { NewDebtSheet, type DebtPreset } from "./Debts.tsx";
import { EmptyState } from "./EmptyState.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

const QUICK_DAYS = [1, 5, 10, 15, 20, 25];

/** Регулярные платежи: список, удаление, добавление (бот напомнит заранее). */
export function RecurringSheet({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const remote = useRemote("recurring", () => api.recurring().then((r) => r.recurring));
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState<number | null>(null);
  const [dayText, setDayText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [converting, setConverting] = useState<{ id: string; preset: DebtPreset } | null>(null);
  const [paying, setPaying] = useState<RecurringDto | null>(null);
  const items = remote.data ?? [];

  const remove = async (r: RecurringDto) => {
    if (!(await confirmDialog(t("confirmDeleteRecurring", { name: r.name }), t("delete")))) return;
    try {
      await api.deleteRecurring(r.id);
      hapticTap();
      toast(t("deletedOk"));
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    }
  };

  const submit = async () => {
    if (busy) return;
    if (!adding) {
      setAdding(true);
      return;
    }
    const value = parseAmount(amount);
    if (!name.trim()) return setError(t("fillName"));
    if (!value) return setError(t("fillAmount"));
    if (!day) return setError(t("pickDay"));
    setBusy(true);
    setError(null);
    try {
      await api.createRecurring({ name: name.trim(), amount: value, dayOfMonth: day });
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      setAdding(false);
      setName("");
      setAmount("");
      setDay(null);
      setDayText("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={`🔔 ${t("recurringTitle")}`} onClose={onClose}>
      {!remote.data && remote.loading ? (
        <div className="skeleton skeleton-block" />
      ) : items.length === 0 && !adding ? (
        <EmptyState icon="🔔" title={t("recurringTitle")} text={t("noRecurring")} />
      ) : (
        <ul className="row-list">
          {items.map((r) => (
            <li key={r.id} className="row">
              <button
                type="button"
                className="template-open"
                onClick={() => {
                  hapticSelect();
                  setPaying(r);
                }}
              >
                <span className="row-icon" aria-hidden="true">
                  🔔
                </span>
                <span className="row-body">
                  <span className="row-title">{r.name}</span>
                  <span className="row-meta">
                    {t("everyMonth", { d: r.dayOfMonth })} · {t("nextOn", { date: `${r.nextDueDate.slice(8, 10)}.${r.nextDueDate.slice(5, 7)}` })}
                  </span>
                </span>
              </button>
              <div className="row-end amount">
                {money(r.amount, r.currency, lang)}
                {r.categoryCode === "loans" && (
                  <button
                    type="button"
                    className="btn-small-ghost to-debt"
                    onClick={() => setConverting({ id: r.id, preset: { name: r.name, monthlyPayment: r.amount, paymentDay: r.dayOfMonth, currency: r.currency } })}
                  >
                    {t("toDebt")}
                  </button>
                )}
              </div>
              <button type="button" className="row-remove" aria-label={t("delete")} onClick={() => void remove(r)}>
                🗑
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="form add-form">
          <h3 className="form-title">{t("addRecurring")}</h3>
          <label className="field">
            <span>{t("recurringName")}</span>
            <input className="input" autoFocus placeholder={t("recurringNamePh")} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </label>
          <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} />
          <div className="field">
            <span>{t("dayOfMonth")}</span>
            <div className="chips" role="radiogroup" aria-label={t("dayOfMonth")}>
              {QUICK_DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={day === d}
                  className={day === d ? "chip active" : "chip"}
                  onClick={() => {
                    hapticSelect();
                    setDay(d);
                    setDayText("");
                  }}
                >
                  {d}
                </button>
              ))}
              <input
                className="input day-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={31}
                placeholder="1–31"
                value={dayText}
                onChange={(e) => {
                  const text = e.target.value.replace(/\D/g, "").slice(0, 2);
                  const n = Number(text);
                  setDayText(text);
                  setDay(n >= 1 && n <= 31 ? n : null);
                }}
                aria-label={t("dayOfMonth")}
              />
            </div>
          </div>
        </div>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <PrimaryAction label={adding ? t("save") : `＋ ${t("addRecurring")}`} onClick={() => void submit()} busy={busy} />
      {paying && <PaySheet payment={{ ...paying, dueDate: paying.nextDueDate }} lang={lang} onClose={() => setPaying(null)} />}
      {converting && (
        <NewDebtSheet
          lang={lang}
          preset={converting.preset}
          onClose={() => setConverting(null)}
          onCreated={() => void api.deleteRecurring(converting.id).then(invalidateRemote)}
        />
      )}
    </Sheet>
  );
}

/** Ближайший платёж: «Оплачено» записывает расход и переносит срок, как кнопка в напоминании бота. */
export function PaySheet({
  payment,
  lang,
  onClose,
}: {
  payment: { id: string; name: string; amount: number; currency: "UZS" | "USD"; dueDate: string };
  lang: Lang;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<"pay" | "skip" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const amount = money(payment.amount, payment.currency, lang);

  const run = async (kind: "pay" | "skip") => {
    if (busy) return;
    if (kind === "skip" && !(await confirmDialog(t("confirmSkip", { name: payment.name }), t("skipOnce")))) return;
    setBusy(kind);
    setError(null);
    try {
      await (kind === "pay" ? api.payRecurring(payment.id) : api.skipRecurring(payment.id));
      hapticTap();
      toast(kind === "pay" ? t("paidOk") : t("skippedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(null);
    }
  };

  return (
    <Sheet title={`🔔 ${payment.name}`} onClose={onClose}>
      <div className="form">
        <div className="pay-amount">{amount}</div>
        <p className="hint">
          {t("dueOn", { date: dayTitle(payment.dueDate, lang) })} · {t("markPaidHint", { amount })}
        </p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button type="button" className="btn btn-block" disabled={busy !== null} onClick={() => void run("skip")}>
          {busy === "skip" ? "…" : t("skipOnce")}
        </button>
        <PrimaryAction label={t("markPaid")} onClick={() => void run("pay")} busy={busy === "pay"} />
      </div>
    </Sheet>
  );
}
